// src/pages/curso/CursoGraduacao.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Container,
  Fade, Paper, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

interface UnidadeCurricular {
  unidadeCurricularId: number; nomeDaUnidadeCurricular: string; ordem: number;
  cargaHoraria: number; cargaHorariaMaxima: number;
}
interface FormaPagamento {
  disponivelParaWeb?: boolean;
  meioDePagamentoDescricao?: string;
  numeroDeParcelas?: number;
  valorDaParcela?: number;
  totalPagamento?: number;
  availableOnline?: boolean;
  method?: string;
  installments?: number;
  installmentValue?: number;
  total?: number;
}
interface Turma {
  id: number | string;
  nomeTurma: string;
  nomeUnidadeOperativa?: string;
  formaDeExecucao?: string;
  cargaHoraria?: number;
  planoDoCursoId?: number;
  informacoesParaVendaOnline?: string;
  informacoesDoPlanoDoCurso?: { nome?: string; objetivos?: string; perfilProfissional?: string; tipoDeAcao?: string; };
  horariosSemAbreviacao?: string[];
  dataPeriodoExecucaoInicialFormatada?: string;
  dataPeriodoExecucaoFinalFormatada?: string;
  recursosFinanceiros?: Array<{ linkMatriculaOnlineDoRecursoFinanceiroNovo?: string }>;
  ofertas?: Array<{
    formasDePagamento?: FormaPagamento[]
  }>;
  linkInteresse?: string;
  linkInscricao?: string;
  linkInscricaoOrigem?: string;
  linkEditalGraduacao?: string | null;
  horarios?: string[]; // 👈 novo campo usado na lógica de turno
  editorial?: any;
}

interface ProcessoSeletivo {
  processoSeletivoId: number;
  nomeVestibular?: string;
  editalNome?: string;
  linkAcesso?: string;
}

const calcularDuracao = (cargaHoraria?: number): string => {
  if (!cargaHoraria) return 'A definir';

  if (cargaHoraria >= 2200) return '2 anos e meio';
  if (cargaHoraria >= 1600) return '2 Anos';

  return `${cargaHoraria} horas`;
};


// 🔁 Helper para normalizar a forma de execução
const normalizeFormaExecucao = (s?: string) => {
  const t = (s ?? '').trim().toLowerCase();
  return t === 'flexível' || t === 'flexivel' ? 'Presencial' : (s ?? '');
};

const currencyBRL = (n: number | string | undefined) => {
  const num = typeof n === 'string' ? Number(n) : (n ?? 0);
  return Number(num).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const editorialHtml = (value?: string) => value
  ? value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />')
  : undefined;

const ConteudoGraduacaoAccordion = ({ curso }: { curso: Turma }) => {
  const [painelAberto, setPainelAberto] = useState<string | false>('sobre');
  const secoes: Array<{ id: string; titulo: string; html?: string; conteudo?: React.ReactNode }> = [
    {
      id: 'sobre',
      titulo: 'Sobre o Curso',
      html: editorialHtml(curso.editorial?.overview) || curso.informacoesParaVendaOnline,
    },
    {
      id: 'objetivos',
      titulo: 'Objetivos',
      html: editorialHtml(curso.editorial?.objectives) || curso.informacoesDoPlanoDoCurso?.objetivos,
    },
    {
      id: 'mercado',
      titulo: 'Mercado de Trabalho',
      html: editorialHtml(curso.editorial?.market) || curso.informacoesDoPlanoDoCurso?.perfilProfissional,
    },
    {
      id: 'coordenacao',
      titulo: 'Coordenação do Curso',
      conteudo: (
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          <Typography>
            <strong>E-mail:</strong>{' '}
            <a href={`mailto:${curso.editorial?.coordinationEmail || 'coordenacao.faculdade@am.senac.br'}`}>{curso.editorial?.coordinationEmail || 'coordenacao.faculdade@am.senac.br'}</a>
          </Typography>
          <Typography>
            <strong>Contato:</strong>{' '}
            <a href={`tel:${curso.editorial?.coordinationPhone || '+559231999956'}`}>{curso.editorial?.coordinationPhone || '(92) 3199-9956'}</a>
          </Typography>
        </Box>
      ),
    },
  ].filter((secao) => Boolean(secao.html || secao.conteudo));

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {secoes.map((secao) => (
        <Accordion
          key={secao.id}
          expanded={painelAberto === secao.id}
          onChange={(_event, expandido) => setPainelAberto(expandido ? secao.id : false)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: painelAberto === secao.id ? '#8fb2e3' : '#dbe4f1',
            borderRadius: '14px !important',
            overflow: 'hidden',
            transition: 'border-color 220ms ease, box-shadow 220ms ease',
            '&::before': { display: 'none' },
            '&.Mui-expanded': { m: 0, boxShadow: '0 10px 28px rgba(16, 42, 94, 0.09)' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#1554a3' }} />}
            aria-controls={`${secao.id}-graduacao-conteudo`}
            id={`${secao.id}-graduacao-cabecalho`}
            sx={{
              minHeight: { xs: 58, sm: 64 },
              px: { xs: 2, sm: 2.5 },
              bgcolor: painelAberto === secao.id ? '#f3f7fd' : '#fff',
              '&.Mui-expanded': { minHeight: { xs: 58, sm: 64 } },
              '& .MuiAccordionSummary-content': { my: 1.5 },
              '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
            }}
          >
            <Typography fontWeight={700} color="#102a5e" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {secao.titulo}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`${secao.id}-graduacao-conteudo`}
            sx={{
              px: { xs: 2, sm: 2.5 },
              pt: 1,
              pb: { xs: 2, sm: 2.5 },
              color: '#344054',
              lineHeight: 1.75,
              overflowWrap: 'anywhere',
              '& p:first-of-type': { mt: 0 },
              '& p:last-of-type': { mb: 0 },
              '& a': { color: '#1554a3' },
            }}
          >
            {secao.html ? <Box dangerouslySetInnerHTML={{ __html: secao.html }} /> : secao.conteudo}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const ResumoGraduacao = ({
  modalidade,
  duracao,
  cargaHoraria,
  unidade,
}: {
  modalidade: string;
  duracao: string;
  cargaHoraria: string;
  unidade: string;
}) => {
  const itens = [
    { titulo: 'Modalidade', valor: modalidade, icon: <SchoolOutlinedIcon /> },
    { titulo: 'Duração', valor: duracao, icon: <CalendarMonthOutlinedIcon /> },
    { titulo: 'Carga horária', valor: cargaHoraria, icon: <TimerOutlinedIcon /> },
    { titulo: 'Unidade', valor: unidade, icon: <LocationOnOutlinedIcon /> },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.5,
        mb: { xs: 3, sm: 4 },
      }}
    >
      {itens.map((item) => (
        <Paper
          key={item.titulo}
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            minHeight: 82,
            borderRadius: 3,
            border: '1px solid rgba(21, 84, 163, 0.12)',
            bgcolor: '#f8fbff',
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              width: 42,
              height: 42,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '14px',
              color: '#1554a3',
              bgcolor: '#fff',
              boxShadow: '0 7px 18px rgba(21, 84, 163, 0.1)',
              '& svg': { fontSize: 22 },
            }}
          >
            {item.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="#667085">{item.titulo}</Typography>
            <Typography fontWeight={750} color="#102a5e" sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
              {item.valor}
            </Typography>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const InvestimentoGraduacao = ({ formas }: { formas: FormaPagamento[] }) => {
  // O conteúdo editorial usa nomes de campos diferentes dos retornados pelo SIG.
  // Aceitar ambos evita que os valores publicados no painel sejam descartados.
  const formasDisponiveis = formas.filter((forma) =>
    forma?.availableOnline ?? forma?.disponivelParaWeb ?? true
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <PaymentsOutlinedIcon sx={{ color: '#1554a3' }} />
        <Box>
          <Typography variant="h5" fontWeight={750} color="#102a5e">Investimento</Typography>
          <Typography variant="body2" color="#667085">Opções de pagamento do semestre</Typography>
        </Box>
      </Box>

      {formasDisponiveis.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {formasDisponiveis.map((forma, index) => {
            const parcelas = forma.numeroDeParcelas ?? forma.installments ?? 0;
            const valorParcela = forma.valorDaParcela ?? forma.installmentValue;
            const total = forma.totalPagamento ?? forma.total;
            const metodo = forma.meioDePagamentoDescricao ?? forma.method;
            const parcelado = Number(parcelas) > 1;
            return (
              <Paper
                key={`${metodo || 'pagamento'}-${index}`}
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(21, 84, 163, 0.14)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f5f8fd 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CreditCardOutlinedIcon sx={{ color: '#1554a3' }} />
                  <Typography fontWeight={700} color="#102a5e">
                    {metodo || 'Forma de pagamento'}
                  </Typography>
                </Box>
                {parcelado ? (
                  <>
                    <Typography variant="h5" fontWeight={800} color="#102a5e">
                      {parcelas}x de {currencyBRL(valorParcela)}
                    </Typography>
                    <Typography color="#667085" sx={{ mt: 1 }}>
                      Total no semestre: <strong>{currencyBRL(total)}</strong>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="#667085">Pagamento à vista</Typography>
                    <Typography variant="h5" fontWeight={800} color="#102a5e">
                      {currencyBRL(total)}
                    </Typography>
                  </>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', color: '#667085' }}>
          Informações de investimento não disponíveis no momento.
        </Paper>
      )}
    </Box>
  );
};

const ctaGraduacaoSx = {
  minHeight: 52,
  px: { xs: 2.5, sm: 5 },
  borderRadius: '16px',
  background: 'linear-gradient(120deg, #124b91 0%, #40368f 52%, #762f80 100%)',
  backgroundSize: '180% 180%',
  backgroundPosition: '0% 50%',
  color: '#fff',
  fontWeight: 750,
  boxShadow: '0 10px 24px rgba(45, 55, 140, 0.2)',
  transition: 'transform 220ms ease, box-shadow 220ms ease, background-position 420ms ease',
  '& .MuiButton-endIcon': { transition: 'transform 220ms ease' },
  '&:hover': {
    background: 'linear-gradient(120deg, #124b91 0%, #40368f 52%, #762f80 100%)',
    backgroundSize: '180% 180%',
    backgroundPosition: '100% 50%',
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 30px rgba(64, 54, 143, 0.28)',
    '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
  },
  '&:active': { transform: 'translateY(0)' },
  '&:focus-visible': { outline: '3px solid rgba(64, 54, 143, 0.28)', outlineOffset: 3 },
};

const LINK_DUVIDAS_GRADUACAO = 'https://docs.google.com/forms/d/1mfdjX5G3esMeU3GdAgj9Q6wM_4IAazNJpsnGozF9ays/viewform?edit_requested=true';
const LINK_INTERESSE_GRADUACAO = 'https://forms.gle/nTVDyv63E1DdxZxX9';

async function getWithFallback(paths: string[], accepts: (data: any) => boolean = () => true) {
  let lastErr: any = null;
  for (const url of paths) {
    try {
      const data = (await axios.get(url, { timeout: 20000 })).data;
      if (accepts(data)) return data;
      lastErr = new Error(`Resposta inválida recebida de ${url}`);
    } catch (e) { lastErr = e; }
  } throw lastErr;
}

// 🔠 helper: força MAIÚSCULAS respeitando PT-BR
const toUpperBR = (s?: string) => (s ?? '').toLocaleUpperCase('pt-BR');

// ⛳ helper: adiciona prefixo “Curso Superior de Tecnologia em ” ao título, evitando duplicar
const addCSTPrefix = (nome?: string) => {
  const n = (nome || '').trim();
  const low = n.toLocaleLowerCase('pt-BR');
  if (low.startsWith('curso superior')) return n;
  if (low.startsWith('bacharelado')) return n;
  if (low.startsWith('licenciatura')) return n;
  return `Curso Superior de Tecnologia em ${n}`;
};

const normalizeBusca = (s?: string) =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');

const nomeCursoBusca = (turma?: Turma | null) =>
  normalizeBusca(turma?.informacoesDoPlanoDoCurso?.nome || turma?.nomeTurma);

const cursoKey = (turma?: Turma | null) =>
  nomeCursoBusca(turma)
    .replace(/^curso superior de tecnologia em\s+/, '')
    .replace(/^curso superior em\s+/, '')
    .replace(/^tecnologia em\s+/, '');

const dataInicioValue = (data?: string): number => {
  const [dia, mes, ano] = (data || '').split('/').map(Number);
  return dia && mes && ano ? Date.UTC(ano, mes - 1, dia) : 0;
};

const valorNumerico = (valor?: number | string): number => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const ordenarOfertasMaisRecentes = (turmas: Turma[]): Turma[] =>
  [...turmas].sort((a, b) =>
    dataInicioValue(b.dataPeriodoExecucaoInicialFormatada) - dataInicioValue(a.dataPeriodoExecucaoInicialFormatada)
    || valorNumerico(b.planoDoCursoId) - valorNumerico(a.planoDoCursoId)
    || valorNumerico(b.id) - valorNumerico(a.id)
  );

const formatarHorarioSemanal = (horario?: string): string | null => {
  const texto = (horario || '').trim();
  if (!texto) return null;

  const partesSemSabado = texto
    .split(/\s*\|\s*|\r?\n/)
    .map(parte => parte.replace(/\s+e\s+s[áa]bado\b.*$/i, '').trim())
    .filter(parte => parte && !normalizeBusca(parte).startsWith('sabado'));
  const textoSemSabado = partesSemSabado
    .join(' ')
    .trim();

  if (!textoSemSabado) return null;

  const dias = normalizeBusca(textoSemSabado);
  const faixa = textoSemSabado.match(/(\d{1,2}:\d{2})\s*(?:às|as|a)\s*(\d{1,2}:\d{2})/i);

  if (dias.includes('segunda-feira') && dias.includes('sexta-feira') && faixa) {
    return `Segunda-feira a Sexta-feira (${faixa[1]} às ${faixa[2]})`;
  }

  return textoSemSabado;
};

const textoProcessoBusca = (processo?: ProcessoSeletivo | null) =>
  normalizeBusca(`${processo?.nomeVestibular || ''} ${processo?.editalNome || ''}`);

const isProcessoIgnorado = (processo?: ProcessoSeletivo | null) => {
  const texto = textoProcessoBusca(processo);
  return (
    !processo?.linkAcesso
    || texto.includes('teste')
    || texto.includes('nao usar')
  );
};

const periodoProcesso = (processo?: ProcessoSeletivo | null) => {
  const texto = textoProcessoBusca(processo);
  let best = 0;
  const semestreRegex = /(20\d{2})\s*[./-]\s*([12])/g;
  let match = semestreRegex.exec(texto);
  while (match) {
    best = Math.max(best, Number(match[1]) * 10 + Number(match[2]));
    match = semestreRegex.exec(texto);
  }
  if (best) return best;

  const anoRegex = /(20\d{2})/g;
  match = anoRegex.exec(texto);
  while (match) {
    best = Math.max(best, Number(match[1]) * 10);
    match = anoRegex.exec(texto);
  }
  return best;
};

const processosMaisRecentes = (processos: ProcessoSeletivo[]) => {
  const validos = processos.filter(p => !isProcessoIgnorado(p));
  const periodoMaisRecente = validos.reduce((max, p) => Math.max(max, periodoProcesso(p)), 0);
  return periodoMaisRecente ? validos.filter(p => periodoProcesso(p) === periodoMaisRecente) : validos;
};

const cidadeTokensDaTurma = (turma?: Turma | null) => {
  const unidade = normalizeBusca(turma?.nomeUnidadeOperativa);
  if (unidade.includes('maues')) return ['maues'];
  if (unidade.includes('itacoatiara')) return ['itacoatiara'];
  if (unidade.includes('parintins')) return ['parintins'];
  if (unidade.includes('faculdade de tecnologia senac')) return ['manaus', 'centro'];
  if (unidade.includes('manaus')) return ['manaus'];
  if (unidade.includes('centro')) return ['centro', 'manaus'];
  return [];
};

const cidadeDaTurma = (turma?: Turma | null): string | null => {
  const unidade = normalizeBusca(turma?.nomeUnidadeOperativa);

  if (unidade.includes('itacoatiara')) return 'Itacoatiara';
  if (unidade.includes('parintins')) return 'Parintins';
  if (unidade.includes('maues')) return 'Maués';
  if (unidade.includes('manaus') || unidade.includes('faculdade de tecnologia senac') || unidade.includes('centro')) {
    return 'Manaus';
  }

  return null;
};

const cidadesDoCurso = (turmas: Turma[]): string[] =>
  Array.from(new Set(turmas.map(cidadeDaTurma).filter((cidade): cidade is string => Boolean(cidade))));

const turnoDaTurma = (turma: Turma): string | null => {
  const horario = normalizeBusca(turma.horarios?.[0] || turma.horariosSemAbreviacao?.[0]);

  if (/18:00.*22:00/.test(horario)) return 'Noturno';
  if (/13:00.*17:00/.test(horario)) return 'Vespertino';
  if (/08:00.*12:00/.test(horario)) return 'Matutino';

  return null;
};

const getLinkIngressoEditorial = (turma: Turma): string | null => {
  const links = Array.isArray(turma.editorial?.admissionLinks)
    ? turma.editorial.admissionLinks
    : [];
  const cidade = normalizeBusca(cidadeDaTurma(turma) || turma.nomeUnidadeOperativa);
  const turno = normalizeBusca(turnoDaTurma(turma) || '');
  const candidatos = links.filter((link: any) =>
    normalizeBusca(link?.city) === cidade && /^https?:\/\//i.test(String(link?.url || ''))
  );
  const exato = candidatos.find((link: any) => normalizeBusca(link?.shift) === turno);
  const generico = candidatos.find((link: any) => !normalizeBusca(link?.shift));
  return exato?.url || generico?.url || null;
};

const cursoKeys = (turma?: Turma | null) => {
  const nome = nomeCursoBusca(turma);
  const keys: string[] = [];
  if (nome.includes('design') && nome.includes('moda')) keys.push('design-moda');
  if (nome.includes('analise') && nome.includes('desenvolvimento') && nome.includes('sistemas')) keys.push('ads');
  if (nome.includes('inteligencia artificial')) keys.push('ia');
  return keys;
};

const processoKeys = (processo?: ProcessoSeletivo | null) => {
  const texto = textoProcessoBusca(processo);
  const keys: string[] = [];
  if (texto.includes('design') && texto.includes('moda')) keys.push('design-moda');
  if (/\bads\b/.test(texto)) keys.push('ads');
  if (/\bia\b/.test(texto) || texto.includes('inteligencia artificial')) keys.push('ia');
  return keys;
};

const getLinkEditalGraduacao = (turma: Turma | null | undefined, processos: ProcessoSeletivo[]) => {
  const cidadeTokens = cidadeTokensDaTurma(turma);
  if (!cidadeTokens.length) return null;

  const turmaKeys = cursoKeys(turma);
  let best: ProcessoSeletivo | null = null;
  let bestScore = -Infinity;

  for (const processo of processosMaisRecentes(processos)) {
    const texto = textoProcessoBusca(processo);
    if (!cidadeTokens.some(token => texto.includes(token))) continue;

    const pKeys = processoKeys(processo);
    if (pKeys.length && !pKeys.some(key => turmaKeys.includes(key))) continue;

    const cursoEspecifico = pKeys.some(key => turmaKeys.includes(key));
    const score = (cursoEspecifico ? 100 : 0) + Number(processo.processoSeletivoId || 0);
    if (score > bestScore) {
      best = processo;
      bestScore = score;
    }
  }

  return best?.linkAcesso || null;
};

const resolveLinkIngressoGraduacao = (turma: Turma, processos: ProcessoSeletivo[]) =>
  getLinkEditalGraduacao(turma, processos)
  || turma.linkEditalGraduacao
  || turma.linkInscricao
  || getLinkIngressoEditorial(turma);

const getVariacoesInscricao = (turmas: Turma[], processos: ProcessoSeletivo[]) => {
  const unicas = new Map<string, Turma>();

  turmas.forEach((turma) => {
    const unidade = normalizeBusca(turma.nomeUnidadeOperativa);
    const unidadeExibida = cidadeDaTurma(turma) || unidade;

    const turno = normalizeBusca(turnoDaTurma(turma) || 'sem-turno');
    const key = `${unidadeExibida}|${turno}`;
    const atual = unicas.get(key);

    // A API pode retornar mais de uma turma para a mesma unidade e turno.
    // Mantem uma opcao visual e prioriza a variacao que possui link valido.
    if (!atual || (!resolveLinkIngressoGraduacao(atual, processos) && resolveLinkIngressoGraduacao(turma, processos))) {
      unicas.set(key, turma);
    }
  });

  return Array.from(unicas.values());
};

const InscricoesGraduacao = ({
  turmas,
  processos,
}: {
  turmas: Turma[];
  processos: ProcessoSeletivo[];
}) => {
  const opcoes = getVariacoesInscricao(turmas, processos);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 4,
        p: { xs: 2.25, sm: 3.5 },
        borderRadius: { xs: 3, sm: 4 },
        border: '1px solid rgba(64, 54, 143, 0.12)',
        background: 'linear-gradient(145deg, #f8faff 0%, #f5f2fb 100%)',
      }}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              flex: '0 0 auto',
              width: 44,
              height: 44,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '14px',
              bgcolor: '#fff',
              color: '#40368f',
              boxShadow: '0 7px 18px rgba(64, 54, 143, 0.1)',
            }}
          >
            <SchoolOutlinedIcon sx={{ fontSize: 23 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={750} color="#102a5e">
              Inscrições Vestibular Senac AM
            </Typography>
            <Typography color="#667085" sx={{ mt: 0.5, lineHeight: 1.6 }}>
              Escolha a cidade e o turno disponíveis para este curso.
            </Typography>
          </Box>
        </Box>

        {opcoes.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {opcoes.map((turma) => {
              const cidade = cidadeDaTurma(turma) || 'Cidade a definir';
              const turno = turnoDaTurma(turma);
              // O servidor ja resolve o edital pela turma (curso, unidade, turno e periodo).
              // O calculo local permanece somente como compatibilidade com caches antigos.
              const href = resolveLinkIngressoGraduacao(turma, processos);
              const texto = turno ? `${cidade} — ${turno}` : cidade;
              const buttonSx = {
                ...ctaGraduacaoSx,
                width: '100%',
                minHeight: 56,
                px: 2.5,
                textAlign: 'center',
              };

              return href ? (
                <Button
                  key={`${String(turma.id)}-${turno || 'turno'}`}
                  variant="contained"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={buttonSx}
                >
                  {texto}
                </Button>
              ) : (
                <Button
                  key={`${String(turma.id)}-${turno || 'turno'}`}
                  variant="contained"
                  disabled
                  sx={buttonSx}
                >
                  {texto}
                </Button>
              );
            })}
          </Box>
        ) : (
          <Typography color="#667085">Nenhuma opção de inscrição disponível no momento.</Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Button
            variant="outlined"
            href={LINK_DUVIDAS_GRADUACAO}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: 46,
              px: 3,
              borderRadius: '14px',
              borderColor: 'rgba(64, 54, 143, 0.38)',
              color: '#40368f',
              fontWeight: 700,
              '&:hover': { borderColor: '#40368f', bgcolor: 'rgba(64, 54, 143, 0.04)' },
            }}
          >
            Dúvidas na matrícula? Fale conosco
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default function CursoGraduacao() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [curso, setCurso] = useState<Turma | null>(null);
  const [matriz, setMatriz] = useState<UnidadeCurricular[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMatriz, setLoadingMatriz] = useState(false);
  const [tab, setTab] = useState(0);
  const [isEmBreve, setIsEmBreve] = useState(false);
  const [variacoes, setVariacoes] = useState<Turma[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [processosSeletivos, setProcessosSeletivos] = useState<ProcessoSeletivo[]>([]);

  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const turmasURLs = process.env.NODE_ENV === 'production'
    ? ['/api/turmas']
    : [`http://${host}:5000/turmas`, `http://${host}:5000/api/turmas`, 'http://localhost:5000/turmas', 'http://localhost:5000/api/turmas', '/api/turmas', '/turmas'];
  const planoURLs = (pid: number | string) => [`/plano-curso/${pid}`, `/api/plano-curso/${pid}`, `http://${host}:5000/plano-curso/${pid}`, `http://${host}:5000/api/plano-curso/${pid}`, 'http://localhost:5000/plano-curso/' + pid, 'http://localhost:5000/api/plano-curso/' + pid];
  const processosURLs = ['/api/ingresso-academico/processo-seletivo/liberados', '/ingresso-academico/processo-seletivo/liberados', `http://${host}:5000/api/ingresso-academico/processo-seletivo/liberados`, `http://${host}:5000/ingresso-academico/processo-seletivo/liberados`, 'http://localhost:5000/api/ingresso-academico/processo-seletivo/liberados', 'http://localhost:5000/ingresso-academico/processo-seletivo/liberados'];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getWithFallback(
          turmasURLs,
          (candidate) => Array.isArray(candidate?.turmasAtivas) || Array.isArray(candidate?.turmasEmBreve)
        );
        const ativas: Turma[] = data?.turmasAtivas || [];
        const emBreve: Turma[] = data?.turmasEmBreve || [];
        let found = ativas.find(c => String(c.id) === String(id)) || null;
        let manual = false;
        if (!found) { found = emBreve.find(c => String(c.id) === String(id)) || null; manual = !!found; }
        if (!found) return navigate('/404');

        const st = (location.state || {}) as { variacoes?: Turma[] };
        const catalogo = manual ? emBreve : ativas;
        const relacionadas = catalogo.filter(c => cursoKey(c) === cursoKey(found));
        const candidatas = Array.from(
          new Map(
            [found, ...relacionadas, ...(Array.isArray(st.variacoes) ? st.variacoes : [])]
              .map(c => [String(c.id), c])
          ).values()
        );
        const ordenadas = ordenarOfertasMaisRecentes(candidatas);
        const ofertaAtual = ordenadas[0] || found;
        const variacoesAtualizadas = ordenadas.filter(v => String(v.id) !== String(ofertaAtual.id));

        if (String(ofertaAtual.id) !== String(id)) {
          navigate(`/curso-graduacao/${ofertaAtual.id}`, {
            replace: true,
            state: { variacoes: variacoesAtualizadas },
          });
          return;
        }

        setCurso(ofertaAtual);
        setIsEmBreve(manual);
        setVariacoes(variacoesAtualizadas);

        if (!manual && tab === 1 && ofertaAtual.planoDoCursoId && matriz.length === 0) {
          setLoadingMatriz(true);
          try {
            const d = await getWithFallback(planoURLs(ofertaAtual.planoDoCursoId));
            const arr: UnidadeCurricular[] =
              (d?.data?.ordemDaUnidadeCurricular || [])
                .sort((a: any, b: any) => a.ordem - b.ordem)
                .map((uc: UnidadeCurricular) => ({
                  ...uc,
                  // ✅ normaliza para MAIÚSCULO ao carregar
                  nomeDaUnidadeCurricular: toUpperBR(uc.nomeDaUnidadeCurricular),
                }));
            setMatriz(arr);
          } finally { setLoadingMatriz(false); }
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getWithFallback(processosURLs);
        const lista: ProcessoSeletivo[] = Array.isArray(data) ? data : (data?.data || []);
        setProcessosSeletivos(lista);
      } catch {
        setProcessosSeletivos([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (!isEmBreve && tab === 1 && curso?.planoDoCursoId && matriz.length === 0) {
        setLoadingMatriz(true);
        try {
          const d = await getWithFallback(planoURLs(curso.planoDoCursoId));
          const arr: UnidadeCurricular[] =
            (d?.data?.ordemDaUnidadeCurricular || [])
              .sort((a: any, b: any) => a.ordem - b.ordem)
              .map((uc: UnidadeCurricular) => ({
                ...uc,
                // ✅ normaliza para MAIÚSCULO ao carregar (segundo caminho)
                nomeDaUnidadeCurricular: toUpperBR(uc.nomeDaUnidadeCurricular),
              }));
          setMatriz(arr);
        } finally { setLoadingMatriz(false); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, curso, isEmBreve]);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress /></Box>;
  if (!curso) return <Typography sx={{ textAlign: 'center', py: 6 }} color="error">Curso não encontrado</Typography>;

  // Para turmas abertas, o SIG e a fonte oficial do investimento. Os valores
  // editoriais sao usados somente como contingencia (por exemplo, em cursos
  // "em breve" que ainda nao possuem uma oferta financeira publicada no SIG).
  // Uma turma pode ter mais de uma oferta, portanto nao devemos limitar a
  // leitura ao primeiro item do array.
  const formasSig = (curso.ofertas || []).flatMap(
    (oferta) => oferta.formasDePagamento || []
  );
  const formas = formasSig.length > 0
    ? formasSig
    : (curso?.editorial?.payments || []);
  const linkInteresse = curso.editorial?.ctaUrl || curso.linkInteresse || LINK_INTERESSE_GRADUACAO;

  // 🕒 Mostra cada combinação de cidade/turno recebida do SIG. Assim, uma
  // oferta noturna não esconde a turma matutina de outra unidade.
  const turmasDoCurso = [curso, ...variacoes].filter(Boolean);
  const allHorarios = turmasDoCurso
    .flatMap((turma) => {
      const horariosTurma = turma.horariosSemAbreviacao?.length
        ? turma.horariosSemAbreviacao
        : (turma.horarios || []);
      const cidade = cidadeDaTurma(turma) || turma.nomeUnidadeOperativa || 'Unidade a definir';
      const turno = turnoDaTurma(turma);
      const rotulo = [cidade, turno].filter(Boolean).join(' — ');
      return horariosTurma
        .map(formatarHorarioSemanal)
        .filter((horario): horario is string => Boolean(horario))
        .map((horario) => `${rotulo}: ${horario}`);
    })
    .filter((horario, index, lista) => lista.indexOf(horario) === index);

  const horarios = !isEmBreve && allHorarios.length > 0
    ? allHorarios.join('\n')
    : (curso?.editorial?.schedule || (allHorarios.length > 0 ? allHorarios.join('\n') : 'A definir'));
  const modalidadeSig = normalizeFormaExecucao(curso.formaDeExecucao);
  const modalidadeExibicao = (!isEmBreve && modalidadeSig) || curso?.editorial?.modality || modalidadeSig || 'Presencial';
  const duracaoExibicao = curso?.editorial?.duration || calcularDuracao(curso.cargaHoraria);
  const cargaHorariaSig = typeof curso.cargaHoraria === 'number' ? `${curso.cargaHoraria} horas` : '';
  const cargaHorariaExibicao = (!isEmBreve && cargaHorariaSig) || curso?.editorial?.workload || cargaHorariaSig || 'A definir';
  const cidadesDisponiveis = cidadesDoCurso(turmasDoCurso);
  const unidadesSig = cidadesDisponiveis.length > 0 ? cidadesDisponiveis.join(', ') : '';
  const unidadeExibicao = (!isEmBreve && unidadesSig) || (curso?.editorial?.units?.length ? curso.editorial.units.join(', ') : '') || unidadesSig || 'A definir';

  const handleTab = (_e: any, v: number) => {
    setTabLoading(true);
    setTimeout(() => { setTab(v); setTabLoading(false); }, 180);
  };

  // 🧾 Título exibido com prefixo (usa nome do plano se houver; senão, nome da turma)
  const tituloExibicao =
    addCSTPrefix(curso.informacoesDoPlanoDoCurso?.nome || curso.nomeTurma);

  return (
    <Container sx={{ py: 5 }}>
      <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 4, bgcolor: '#2b6cb0', '&:hover': { bgcolor: '#2c5282' } }}>
        Voltar para Cursos
      </Button>

      {/* 🔤 Título concatenado com “Curso Superior de Tecnologia em ” */}
      <Typography
        variant="h4"
        fontWeight={600}
        textAlign="center"
        gutterBottom
        sx={{ fontSize: { xs: '1.55rem', sm: '2.125rem' }, lineHeight: 1.25, mb: { xs: 2.5, sm: 3.5 } }}
      >
        {tituloExibicao}
      </Typography>

      <ResumoGraduacao
        modalidade={modalidadeExibicao}
        duracao={duracaoExibicao}
        cargaHoraria={cargaHorariaExibicao}
        unidade={unidadeExibicao}
      />

      {isEmBreve ? (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, mb: 5, borderRadius: { xs: 2.5, sm: 3 } }}>
          <ConteudoGraduacaoAccordion curso={curso} />
          <InvestimentoGraduacao formas={formas} />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>
            <Button
              variant="contained"
              sx={{ ...ctaGraduacaoSx, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 320 } }}
              href={linkInteresse}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<ArrowForwardIcon />}
            >
              {curso.editorial?.ctaLabel || 'Registre seu Interesse'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={handleTab}
            variant="fullWidth"
            sx={{
              mb: 2,
              p: 0.5,
              borderRadius: 3,
              bgcolor: '#f2f5fa',
              '& .MuiTab-root': { minHeight: 48, borderRadius: 2, fontWeight: 700 },
              '& .Mui-selected': { bgcolor: '#fff', color: '#102a5e', boxShadow: '0 5px 16px rgba(16, 42, 94, 0.08)' },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab label="Visão Geral" />
            <Tab label="Matriz Curricular" />
          </Tabs>

          <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, mb: 5, minHeight: 250, borderRadius: { xs: 2.5, sm: 3 } }}>
            {tabLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress /></Box>
            ) : (
              <Fade in timeout={300}>
                <Box>
                  {tab === 0 && (
                    <Box>
                      <ConteudoGraduacaoAccordion curso={curso} />

                      <Paper
                        elevation={0}
                        sx={{
                          mt: 4,
                          p: { xs: 2.25, sm: 3 },
                          borderRadius: 3.5,
                          border: '1px solid rgba(21, 84, 163, 0.1)',
                          background: 'linear-gradient(145deg, #f9fbff 0%, #f1f6fc 100%)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: '13px',
                              bgcolor: '#fff',
                              color: '#1554a3',
                              boxShadow: '0 7px 18px rgba(21, 84, 163, 0.1)',
                            }}
                          >
                            <AccessTimeRoundedIcon sx={{ fontSize: 21 }} />
                          </Box>
                          <Typography variant="h6" fontWeight={750} color="#102a5e">Horários das aulas</Typography>
                        </Box>
                        <Typography color="#475467" sx={{ whiteSpace: 'pre-line', lineHeight: 1.75 }}>
                          {horarios}
                        </Typography>
                      </Paper>

                      <InvestimentoGraduacao formas={formas} />

                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>
                        <Button
                          variant="contained"
                          sx={{ ...ctaGraduacaoSx, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 320 } }}
                          href={linkInteresse}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<ArrowForwardIcon />}
                        >
                          {curso.editorial?.ctaLabel || 'Registre seu Interesse'}
                        </Button>
                      </Box>

                      <InscricoesGraduacao
                        turmas={[curso, ...variacoes]}
                        processos={processosSeletivos}
                      />
                    </Box>
                  )}
                  {tab === 1 && (
                    <Box>
                      <Typography variant="h5" fontWeight={750} color="#102a5e" gutterBottom>Matriz Curricular</Typography>
                      <Typography color="#667085" sx={{ mb: 2.5 }}>
                        Componentes curriculares organizados conforme o plano de curso vigente.
                      </Typography>
                      {loadingMatriz ? <Box py={2}><CircularProgress /></Box> : matriz.length > 0 ? (
                        <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                          <Table size="small" sx={{ minWidth: 620 }}>
                            <TableHead sx={{ bgcolor: '#f3f7fd' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#102a5e' }}>Ordem</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#102a5e' }}>Unidade Curricular</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#102a5e', whiteSpace: 'nowrap' }}>Carga Horária</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {matriz.map(uc => (
                                <TableRow key={uc.unidadeCurricularId} hover>
                                  <TableCell>{uc.ordem}</TableCell>
                                  <TableCell>{toUpperBR(uc.nomeDaUnidadeCurricular)}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{uc.cargaHoraria}h</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : <Typography>Matriz curricular não disponível</Typography>}
                    </Box>
                  )}
                </Box>
              </Fade>
            )}
          </Paper>

        </>
      )}
    </Container>
  );
}
